import axios, { AxiosResponse, AxiosError } from 'axios';
import { IApiConfiguration, ICacheConfig, IEndPointArgs, ApiResponse } from './interfaces';
import { ErrorResponse } from '../../file';
import { notifySessionExpired } from './session';
import { getChatToken } from './tokenStore';

const axiosInstance = axios.create({
   baseURL: process.env.REACT_APP_BASE_URL,
   headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*'
   },
   timeout: 300000
});

declare module 'axios' {
   interface AxiosRequestConfig {
      /** Opt out of duplicate-request blocking (safe, idempotent reads). */
      skipDedupe?: boolean;
      /** Internal: key under which this request is tracked in `pendingRequests`. */
      dedupeKey?: string;
   }
}

const pendingRequests = new Set<string>();

/**
 * Two requests are duplicates when method + url match and, for chat sends, the
 * `clientTurnId` matches too — so two different messages can be in flight at
 * once, while a double-submit of the same turn is still blocked.
 */
const getRequestKey = (method?: string, url?: string, data?: unknown): string => {
   const turnId = data && typeof data === 'object' ? (data as { clientTurnId?: unknown }).clientTurnId : undefined;
   return `${method}:${url}${typeof turnId === 'string' ? `:${turnId}` : ''}`;
};

axiosInstance.interceptors.request.use(
   (config) => {
      if (config.skipDedupe) return config;

      const requestKey = getRequestKey(config.method, config.url, config.data);
      if (pendingRequests.has(requestKey)) {
         return Promise.reject(new axios.Cancel(`Duplicate request blocked: ${requestKey}`));
      }
      pendingRequests.add(requestKey);
      config.dedupeKey = requestKey;
      return config;
   },
   (error) => Promise.reject(error)
);

axiosInstance.interceptors.request.use(
   (config) => {
      const token = getChatToken();
      if (token) {
         config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
   },
   (error) => {
      return Promise.reject(error);
   }
);

axiosInstance.interceptors.response.use(
   (response: AxiosResponse) => {
      if (response.config.dedupeKey) pendingRequests.delete(response.config.dedupeKey);
      return response;
   },
   (error: AxiosError<ErrorResponse>) => {
      if (error.config?.dedupeKey) pendingRequests.delete(error.config.dedupeKey);

      // The server rejected the chat token (expired or revoked): end the session instead of failing silently.
      if (error.response?.status === 401 && error.config?.headers?.Authorization) {
         notifySessionExpired();
      }

      // Transform the error to include ErrorResponse structure
      if (error.response?.data) {
         return Promise.reject({
            ...error,
            errorResponse: error.response.data as ErrorResponse
         });
      }

      return Promise.reject(error);
   }
);

const createEndPoint = (endPoint: string, args?: IEndPointArgs): string => {
   let tempEndPoint = endPoint;
   tempEndPoint = tempEndPoint?.replace(/{(\w+)}/g, (_, key) => args?.urlParms?.[key] ?? '');

   const handleParams = () => {
      const keys = Object?.keys(args?.queryParams ?? {});
      if (!keys || keys?.length === 0) return;
      tempEndPoint = tempEndPoint.concat('?');
      keys.forEach((key) => {
         let hasAtLeastOneParam = false;
         if (typeof args?.queryParams[key] === 'string' || typeof args?.queryParams[key] === 'boolean' || typeof args?.queryParams[key] === 'number') {
            tempEndPoint = tempEndPoint.concat(`${key}=${args?.queryParams[key]}&`);
            hasAtLeastOneParam = true;
         } else if (Array.isArray(args?.queryParams[key])) {
            args?.queryParams[key].forEach((param: any) => {
               tempEndPoint = tempEndPoint.concat(`${hasAtLeastOneParam ? '&' : ''}${key}=${param}&`);
            });
         }
      });
   };
   handleParams();
   return tempEndPoint;
};

export const getQueriesFromUrlParams = (params: any) => {
   const queries = params?.map((x: string) => {
      const y = decodeURIComponent(params?.find((y: string) => y.includes(x)));
      const data = y?.split(':');
      console.log(data?.[1]?.split('&'));
      return { [`${data?.[0]}`]: data?.[1]?.split('&') };
   });
   return queries?.reduce((acc: any, obj: any) => ({ ...acc, ...obj }), {});
};

// Export error handling utilities
export * from './errorUtils';
export type { ApiResponse } from './interfaces';

export const axiosCall = async <T>({
   endpoint,
   args,
   cacheConfig
}: {
   endpoint: IApiConfiguration<T>;
   args?: IEndPointArgs;
   cacheConfig?: ICacheConfig;
}): Promise<ApiResponse<T>> => {
   try {
      let resp: AxiosResponse<T>;
      const requestConfig = {
         skipDedupe: endpoint?.skipDedupe,
         ...(endpoint?.timeoutMs && { timeout: endpoint.timeoutMs }),
         // Named so the JSON default does not turn the form into JSON; the browser then adds the boundary itself
         ...(endpoint?.multipart && { headers: { 'Content-Type': 'multipart/form-data' } })
      };

      switch (endpoint?.method) {
         case 'GET':
            resp = await axiosInstance.get<T>(createEndPoint(endpoint?.endpoint, args), requestConfig);
            break;
         case 'POST':
            resp = await axiosInstance.post<T>(createEndPoint(endpoint?.endpoint, args), args?.httpBody, requestConfig);
            break;
         case 'PUT':
            resp = await axiosInstance.put<T>(createEndPoint(endpoint?.endpoint, args), args?.httpBody, requestConfig);
            break;
         case 'DELETE':
            resp = await axiosInstance.delete<T>(createEndPoint(endpoint?.endpoint, args), requestConfig);
            break;
         default:
            throw new Error(`Unsupported HTTP method: ${endpoint?.method}`);
      }

      return { data: resp.data, error: undefined };
   } catch (error: any) {
      return { data: undefined, error: toErrorResponse(error) };
   }
};

/**
 * Always yields an `ErrorResponse`, also for failures without a server body
 * (offline, timeout, CORS, blocked duplicate), which used to surface as an
 * `undefined` error and therefore looked like a success to callers.
 */
const toErrorResponse = (error: any): ErrorResponse => {
   if (error?.errorResponse) {
      return error.errorResponse as ErrorResponse;
   }

   if (axios.isCancel(error)) {
      return { type: 'Cancelled', title: 'REQUEST_CANCELLED', message: error?.message };
   }

   const status: number | undefined = error?.response?.status;
   if (status) {
      return { type: 'HttpError', title: `HTTP_${status}`, message: `Request failed with status ${status}`, status };
   }

   return { type: 'NetworkError', title: 'NETWORK_ERROR', message: 'Network error. Please check your connection and try again.', status: 0 };
};
